import type { Table } from '@/types'

export const TW = 244
export const HDR = 29
export const COL = 22
export const FTR = 28
export const MIN_TABLE_WIDTH = TW

export interface Point {
  x: number
  y: number
}

export interface ViewTransform {
  sc: number
  tx: number
  ty: number
}

export interface CanvasRect {
  left: number
  top: number
}

export interface TableBounds {
  x: number
  y: number
  w: number
  h: number
}

export function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value))
}

export function calcTableHeight(colCount: number): number {
  return HDR + colCount * COL + FTR
}

export function getTableWidth(table: Pick<Table, 'width'>): number {
  return Math.max(MIN_TABLE_WIDTH, table.width ?? TW)
}

export function getTableHeight(table: Pick<Table, 'cols'>): number {
  return calcTableHeight(table.cols.length)
}

export function calcColumnY(tableY: number, colIndex: number): number {
  return tableY + HDR + colIndex * COL + COL / 2
}

export function getTableBounds(
  table: Pick<Table, 'x' | 'y' | 'width' | 'cols'>,
  width?: number,
): TableBounds {
  return {
    x: table.x,
    y: table.y,
    w: width ?? getTableWidth(table),
    h: getTableHeight(table),
  }
}

export function rectsIntersect(a: TableBounds, b: TableBounds): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
}

export function getColumnIndex(
  table: Pick<Table, 'cols'>,
  columnName: string,
): number {
  return table.cols.findIndex((column) => column.name === columnName)
}

export function getColumnCenterY(
  table: Pick<Table, 'y' | 'cols'>,
  columnName: string,
): number {
  const columnIndex = getColumnIndex(table, columnName)
  return calcColumnY(table.y, Math.max(columnIndex, 0))
}

export function clientToCanvas(
  clientX: number,
  clientY: number,
  canvasRect: CanvasRect,
  view: ViewTransform,
): Point {
  return {
    x: (clientX - canvasRect.left) / view.sc - view.tx,
    y: (clientY - canvasRect.top) / view.sc - view.ty,
  }
}

export function canvasToClient(
  canvasX: number,
  canvasY: number,
  canvasRect: CanvasRect,
  view: ViewTransform,
): Point {
  return {
    x: canvasRect.left + view.sc * (canvasX + view.tx),
    y: canvasRect.top + view.sc * (canvasY + view.ty),
  }
}
